import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FliptProvider, useFliptContext, useFliptSelector } from '@flipt-io/flipt-client-react';
import { FeatureFlagContext } from './FeatureFlagContext';

/**
 * 스냅샷 갱신 주기(초). Flipt 서버를 이 간격으로 폴링한다.
 *
 * 플래그는 실시간 반영이 필요한 종류가 아니다 — 켜고 끈 값이 5분 안에 퍼지면
 * 충분하고, 그 대가로 CloudFront 뒤 Flipt 호출이 절반으로 준다. _app 에 provider 가
 * 올라가 전 페이지에서 폴링하게 되면서 이 비용이 실제로 커졌다.
 *
 * 폴링 실패는 client.refresh() 가 error 를 세팅해 평가를 fallback 으로 되돌리는
 * 경로이기도 하다(FeatureFlagBridge 의 onError 주석). 주기를 늘리면 그 표면도 같이
 * 줄어든다.
 */
const DEFAULT_UPDATE_INTERVAL = 300;

/**
 * Maximum Flipt initialization attempts on supported browsers.
 *
 * The SDK counts the initial attempt, then waits 1s, 2s, 4s, and 8s before
 * attempts 2–5, so a permanently failing client gives up at about 15 seconds
 * instead of retrying every 30 seconds for the lifetime of the tab.
 *
 * 15초는 FeatureFlagBridge 가 화면을 기본값으로 넘기는 10초(STORE_SYNC_MAX_ATTEMPTS)
 * 바로 뒤다 — 늦게 성공하면 useFliptBoolean 구독으로 실제 값이 반영되므로, 이 간격이
 * 좁을수록 "기본값 → 실제값" 으로 화면이 바뀌는 창이 짧다.
 */
const DEFAULT_MAX_RETRY_ATTEMPTS = 5;

/**
 * No-op fetcher that returns an empty flag snapshot without network calls.
 * Used when `disabled` is true — WASM initializes with zero flags,
 * so all useFliptBoolean/useFliptVariant calls safely return their fallback values.
 *
 * Returns a real Response object (not a duck-typed mock) so that
 * any Flipt SDK method (.text(), .clone(), .headers.entries(), etc.) works safely.
 */
const NOOP_FETCHER = async () => new Response(JSON.stringify({
  namespace: {
    key: ''
  },
  flags: []
}), {
  status: 200,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Polling interval (ms) for the isLoading sync workaround.
 *
 * `useFliptContext()` is a plain `useContext` — it hands back the mutable
 * `storeRef.current` without subscribing. Only `useFliptSelector` subscribes.
 * So when the SDK flips `isLoading` and calls `notify()`, this component is
 * never re-rendered and `isLoading` stays stuck at true. We poll the raw store
 * to detect the mismatch and force a re-render.
 */
const STORE_SYNC_POLL_MS = 500;

/** Maximum polling attempts before giving up (500ms × 20 = 10s). */
const STORE_SYNC_MAX_ATTEMPTS = 20;

/**
 * Smallest valid WASM module whose function signature uses `externref`.
 *
 * Flipt's engine requires WebAssembly reference types. Some Chromium-based
 * vendor browsers expose WebAssembly but reject `externref`, so checking only
 * for `window.WebAssembly` still lets initialization throw a CompileError.
 */
const WASM_REFERENCE_TYPES_PROBE = new Uint8Array([0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00, 0x01, 0x05, 0x01, 0x60, 0x01, 0x6f, 0x00]);
function supportsWasmReferenceTypes(): boolean {
  return typeof WebAssembly !== 'undefined' && typeof WebAssembly.validate === 'function' && WebAssembly.validate(WASM_REFERENCE_TYPES_PROBE);
}

/**
 * Selects the *terminal* `error` out of the Flipt store.
 *
 * The SDK assigns `error` and notifies on every failed init attempt, including
 * the ones it is about to retry (`isLoading` stays true while retrying). Only
 * when it gives up does it also flip `isLoading` to false. Reporting while
 * `isLoading` is true would count every transient blip that the retry then
 * recovers from — Sentry could not tell a recovered session from a stuck one.
 * Dropping the error until `isLoading` is false leaves exactly two reportable
 * cases: init exhausted all retries, or a later `refresh()` poll failed.
 *
 * Declared at module scope on purpose — an inline selector would be a new
 * function every render, replacing `useSyncExternalStore`'s getSnapshot each
 * time. Returning only the error also means a re-render happens *only* when
 * the error changes, not on every store notification.
 */
const selectFliptError = (_client: unknown, isLoading: boolean, error: Error | null) => isLoading ? null : error;
export interface FeatureFlagProviderProps {
  /** Feature flag server URL */
  serverUrl: string;
  /** Flipt namespace for flag scoping */
  namespace: string;
  /** Environment (e.g., 'development', 'live') */
  environment: string;
  /** User-level ID for flag evaluation — user hashId (play) or quizUserId (quiz).
   *  Empty string when not logged in. Combined with deviceId as fallback internally. */
  userId: string;
  /** Device-level ID — stable across login/logout, used as WASM instance key and device bucketing */
  deviceId: string;
  /** Polling interval in seconds (default: 300) */
  updateInterval?: number;
  /** Pre-evaluated boolean flag values from SSR (seed) */
  ssrFlags?: Record<string, boolean>;
  /** Pre-evaluated variant flag values from SSR (seed) */
  ssrVariants?: Record<string, string>;
  /** Reports Flipt failures to the host app's error tracker.
   *  The package never picks a logger — the app injects one. Deduped by error
   *  message, so a repeatedly failing poll reports once rather than every
   *  `updateInterval`.
   *
   *  Fires for: init failure after the SDK has exhausted its retries (WASM chunk
   *  load, server down, 4xx/5xx, network), and `client.refresh()` failure during
   *  polling. Failures the SDK is still retrying are not reported. In every
   *  case evaluation falls back to its default value, so this is observability
   *  only — nothing about the user-facing behavior changes. */
  onError?: (error: unknown) => void;
  /** When true, skips Flipt server fetch entirely (noop fetcher).
   *  WASM initializes with empty flags — all evaluations return fallback values.
   *  Use for contexts where feature flags are unnecessary (e.g., iframe embeds). */
  disabled?: boolean;
  children: React.ReactNode;
}

/**
 * React provider that wraps @flipt-io/flipt-client-react's FliptProvider
 * while preserving our custom interface (ssrFlags, ssrVariants, userId, deviceId, isReady).
 *
 * Props are flat primitives to avoid reference instability from config objects.
 * An inner bridge component reads Flipt's isLoading state once and provides it
 * via our context, preventing N-subscription overhead from multiple hooks.
 *
 * Targeting context (e.g., spaceHashId) is NOT managed here — each domain hook
 * passes its own context via the per-call `context` option in useFeatureFlag.
 */
export function FeatureFlagProvider({
  serverUrl,
  namespace,
  environment,
  userId,
  deviceId,
  updateInterval = DEFAULT_UPDATE_INTERVAL,
  ssrFlags,
  ssrVariants,
  onError,
  disabled = false,
  children
}: FeatureFlagProviderProps) {
  const userEntityId = userId || deviceId;
  const deviceEntityId = deviceId;
  // 예전엔 deviceId 가 없으면 FliptProvider 를 아예 건너뛰고 children 만 렌더했는데,
  // 그러면 소비 코드의 useFeatureFlag 가 "must be used within a FliptProvider" 로
  // **throw** 한다 — 서버 렌더에서는 getDeviceId() 가 쿠키 없이 항상 비므로
  // SSG/SSR 페이지가 통째로 깨진다. context 는 늘 제공하고, 나가는 요청만 막는다.
  //
  // 조건은 deviceId 유무가 아니라 **서버인지**다. 스냅샷 fetch 는 네임스페이스 단위라
  // entityId 와 무관하고(평가만 WASM 이 entityId 로 한다), 그래서 entityId 가 없다고
  // fetch 를 막을 이유가 없다. 서버에서는 어차피 effect 가 돌지 않아 WASM 도 안 뜬다.
  const isServer = typeof window === 'undefined';
  const shouldSkipFetch = disabled || isServer;
  const shouldSkipWasmInitialization = !isServer && !supportsWasmReferenceTypes();
  const fliptOptions = useMemo(() => ({
    namespace,
    url: serverUrl,
    environment,
    updateInterval,
    ...(shouldSkipFetch ? {
      fetcher: NOOP_FETCHER
    } : {}),
    maxRetryAttempts: shouldSkipWasmInitialization ? 0 : DEFAULT_MAX_RETRY_ATTEMPTS
  }), [namespace, serverUrl, environment, updateInterval, shouldSkipFetch, shouldSkipWasmInitialization]);
  return <FliptProvider key={`${deviceEntityId}-${shouldSkipFetch}`} options={fliptOptions} data-sentry-element="FliptProvider" data-sentry-component="FeatureFlagProvider" data-sentry-source-file="FeatureFlagProvider.tsx">
      <FeatureFlagBridge userEntityId={userEntityId} deviceEntityId={deviceEntityId} shouldSkipWasmInitialization={shouldSkipWasmInitialization} ssrFlags={ssrFlags} ssrVariants={ssrVariants} onError={onError} data-sentry-element="FeatureFlagBridge" data-sentry-source-file="FeatureFlagProvider.tsx">
        {children}
      </FeatureFlagBridge>
    </FliptProvider>;
}

/**
 * Inner bridge that reads useFliptContext() exactly once,
 * then exposes isReady via our FeatureFlagContext.
 * This prevents N hooks from each subscribing to Flipt independently.
 *
 * Includes a workaround for how @flipt-io/flipt-client-react exposes its store:
 * `useFliptContext()` is a plain `useContext` with no subscription, so the
 * store's `isLoading` transition never re-renders this component. We poll the
 * raw store object to detect the mismatch and force a re-render. Errors take
 * the other route — `useFliptSelector`, which does subscribe.
 */
function FeatureFlagBridge({
  userEntityId,
  deviceEntityId,
  shouldSkipWasmInitialization,
  ssrFlags,
  ssrVariants,
  onError,
  children
}: {
  userEntityId: string;
  deviceEntityId: string;
  shouldSkipWasmInitialization: boolean;
  ssrFlags?: Record<string, boolean>;
  ssrVariants?: Record<string, string>;
  onError?: (error: unknown) => void;
  children: React.ReactNode;
}) {
  const fliptCtx = useFliptContext();
  const {
    isLoading
  } = fliptCtx;

  // Workaround: poll the raw store to detect stale useSyncExternalStore state.
  // The fliptCtx object is a mutable ref (storeRef.current from FliptProvider),
  // so reading its properties in setInterval gives the live value even when
  // useSyncExternalStore fails to notify React.
  const [syncFix, setSyncFix] = useState(0);
  const [hasGivenUp, setHasGivenUp] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const pollCountRef = useRef(0);
  useEffect(() => setHasMounted(true), []);
  useEffect(() => {
    if (shouldSkipWasmInitialization) return;
    if (!isLoading) return; // already synced, no need to poll
    if (syncFix > 0) return; // workaround already fired, stop polling
    if (hasGivenUp) return; // already gave up, stop polling

    pollCountRef.current = 0;
    const id = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current >= STORE_SYNC_MAX_ATTEMPTS) {
        clearInterval(id);
        console.warn(`[@zep/feature-flag] useSyncExternalStore shim polling gave up after ${STORE_SYNC_MAX_ATTEMPTS} attempts. ` + 'Flipt WASM may have failed to initialize. SSR seed values will continue to be used.');
        setHasGivenUp(true);
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw = fliptCtx as any;
      if (raw.client && raw.isLoading === false) {
        setSyncFix(n => n + 1); // force re-render to pick up new state
      }
    }, STORE_SYNC_POLL_MS);
    return () => clearInterval(id);
  }, [isLoading, fliptCtx, syncFix, hasGivenUp, shouldSkipWasmInitialization]);

  // Treat as ready when: Flipt initialized successfully, sync workaround fired,
  // polling gave up, or the browser cannot compile Flipt's WASM. Keep the first
  // client render loading to match SSR, then expose SSR seed/default values.
  const effectiveIsLoading = shouldSkipWasmInitialization && hasMounted ? false : hasGivenUp ? false : syncFix > 0 ? (fliptCtx as unknown as Record<string, unknown>).isLoading !== false : isLoading;

  // Flipt 실패를 호스트 앱의 에러 트래커로 흘린다. 실패해도 평가는 기본값으로
  // degrade 하므로 화면은 멀쩡하고, 그래서 계측이 없으면 장애가 조용히 지나간다 —
  // 실험 분석에서는 그 구간이 통째로 대조군처럼 보인다.
  //
  // 반드시 useFliptSelector 로 읽는다. useFliptContext() 는 구독이 없어서
  // (위 STORE_SYNC_POLL_MS 주석) SDK 가 error 를 세팅하고 notify() 해도 이
  // 컴포넌트가 리렌더되지 않는다 — 그러면 보고는 부모가 우연히 리렌더될 때만
  // 일어나고, 랜딩만 보고 이탈하는 방문자에겐 영영 보고되지 않는다.
  const fliptError = useFliptSelector(selectFliptError);

  // 같은 원인의 실패는 한 번만 보낸다. 객체 신원으로 비교하면 안 된다 —
  // refresh() 실패는 폴링마다 rejection 을 그대로 대입해서 내용이 같아도 매번
  // 새 Error 객체다. 그대로 두면 탭을 열어둔 시간만큼 같은 에러가 쌓인다.
  const reportedErrorKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!fliptError) return;
    const key = fliptError.message || String(fliptError);
    if (key === reportedErrorKeyRef.current) return;
    reportedErrorKeyRef.current = key;
    onError?.(fliptError);
  }, [fliptError, onError]);

  // SSR seed values: capture once at mount — after Flipt initializes,
  // WASM evaluation replaces these via useFliptBoolean/useFliptVariant defaults.
  const ssrFlagsRef = useRef(ssrFlags);
  const ssrVariantsRef = useRef(ssrVariants);
  const contextValue = useMemo(() => ({
    userEntityId,
    deviceEntityId,
    isReady: !effectiveIsLoading,
    ssrFlags: ssrFlagsRef.current,
    ssrVariants: ssrVariantsRef.current
  }), [userEntityId, deviceEntityId, effectiveIsLoading]);
  return <FeatureFlagContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="FeatureFlagBridge" data-sentry-source-file="FeatureFlagProvider.tsx">
      {children}
    </FeatureFlagContext.Provider>;
}